const getExportKeys = <T extends object>(data: T[]) =>
  Array.from(
    data.reduce((set, obj) => {
      Object.keys(obj ?? {}).forEach((k) => {
        if (k === "translations") return;
        set.add(k);
      });
      return set;
    }, new Set<string>())
  );

const normalizeExportValue = (value: unknown): string => {
  if (value == null) return "";

  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    if (value.every((v) => v && typeof v === "object" && "id" in v)) {
      return value
        .map((v) => String((v as { id?: unknown }).id ?? ""))
        .join("|");
    }
    return JSON.stringify(value);
  }

  if (typeof value === "object") {
    if ("id" in value) return String((value as { id?: unknown }).id ?? "");
    return JSON.stringify(value);
  }

  return String(value);
};

const withExtension = (filename: string, extension: string) =>
  /\.[^.]+$/.test(filename)
    ? filename.replace(/\.[^.]+$/, extension)
    : `${filename}${extension}`;

export function downloadCSV<T extends object>(
  data: T[],
  filename = "data.csv"
) {
  if (!data || data.length === 0) return;

  const keys = getExportKeys(data);

  const escapeCSV = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const header = keys.join(";");
  const rows = data.map((row) =>
    keys
      .map((k) =>
        escapeCSV(normalizeExportValue((row as Record<string, unknown>)[k]))
      )
      .join(";")
  );
  const csv = [header, ...rows].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadExcel<T extends object>(
  data: T[],
  filename = "data.xlsx"
) {
  if (!data || data.length === 0) return;

  const ExcelJS = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Datos");
  const keys = getExportKeys(data);

  worksheet.columns = keys.map((key) => ({
    header: key,
    key,
    width: Math.min(Math.max(key.length + 2, 12), 40),
  }));

  data.forEach((row) => {
    worksheet.addRow(
      Object.fromEntries(
        keys.map((key) => [
          key,
          normalizeExportValue((row as Record<string, unknown>)[key]),
        ])
      )
    );
  });

  if (keys.length > 0) {
    worksheet.views = [{ state: "frozen", ySplit: 1 }];
    worksheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: keys.length },
    };
    worksheet.getRow(1).font = { bold: true };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer as BlobPart], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = withExtension(filename, ".xlsx");
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadPDF<T extends object>(
  data: T[],
  filename = "data.pdf",
  title = "Datos"
) {
  if (!data || data.length === 0) return;

  const [{ jsPDF }, autoTableModule] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  const autoTable = autoTableModule.default;
  const keys = getExportKeys(data);
  const doc = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: keys.length > 6 ? "landscape" : "portrait",
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(title, 14, 15);

  autoTable(doc, {
    head: [keys],
    body: data.map((row) =>
      keys.map((key) =>
        normalizeExportValue((row as Record<string, unknown>)[key])
      )
    ),
    startY: 22,
    styles: { fontSize: 7, cellPadding: 1.5, overflow: "linebreak" },
    headStyles: { fillColor: [0, 105, 131], textColor: 255 },
    margin: { top: 16, right: 10, bottom: 12, left: 10 },
  });

  doc.save(withExtension(filename, ".pdf"));
}
